import { BoardConst, BOARD_X, BOARD_Y, DIRECTION_KEYS, DIRECTION_VECTORS, DIRECTION_STEPS, DIRECTION_TURN, TURN_MSB_VECTORS, get_ray_direction } from './rules_spec/constants.js';
import { BoardState } from './bitboard.js';

export function get_turn_point(direction: BoardConst, tx: number, ty: number, sx: number, sy: number): [number, number] {
    const direction_i = direction as number;
    // 奇数: 斜め開始
    if (direction_i & 1) {
        // 3,7: 1本目は x - y が不変
        if (direction_i & 2) {
            const a = sx - sy;
            const b = tx + ty;
            return [(a + b) >> 1, (b - a) >> 1];
        }
        // 1,5: 1本目は x + y が不変
        const a = sx + sy;
        const b = tx - ty;
        return [(a + b) >> 1, (a - b) >> 1];
    }
    // 偶数: 縦横開始
    // 2,6: 横開始
    if (direction_i & 2) {
        return [tx, sy];
    }
    // 0,4: 縦開始
    return [sx, ty];
}

export function is_turn_msb(direction: BoardConst, turn_x: number, turn_y: number, target_x: number, target_y: number): boolean {
    const [msb_dx, msb_dy] = TURN_MSB_VECTORS[direction];
    const dx = target_x - turn_x;
    const dy = target_y - turn_y;
    return (dx * msb_dx + dy * msb_dy) > 0;
}

export class DoubleRayMove {
    bb: BoardState;
    pid: number;
    directions: BoardConst[];
    N_directions: number;
    direction_vectors: Array<[number, number]>;
    direction_steps: number[];
    is_piece_mask: number;

    scan_funcs: Array<() => number>;
    turn_scan_funcs: Array<(x: number, y: number, rank: number) => [number, number]>;
    turn_direction_steps: Array<[number, number]>;

    xy_bits: number;
    xy_mask: number;
    max_pid_gote: number;
    board16: Int16Array;
    pieceSq16: Int16Array;

    x!: number;
    y!: number;
    board_x!: number;
    index!: number;

    turn_distances!: number[];
    after_turn_distances!: Array<Array<[number, number]>>;
    move_dir_mask!: Map<number, number>;
    reach_edge!: boolean[];

    constructor(bitboard: BoardState, pid: number, directions: BoardConst[]) {
        this.bb = bitboard;
        this.pid = pid;

        this.directions = directions;
        this.N_directions = this.directions.length;
        this.direction_vectors = this.directions.map(d => DIRECTION_VECTORS[d]);
        this.direction_steps = this.directions.map(d => DIRECTION_STEPS[d]);

        this.is_piece_mask = 16;

        this.scan_funcs = this.directions.map(d => () => this.bb.scan_direction(this.x, this.y, d, 0));

        this.turn_scan_funcs = this.directions.map(d => {
            const turnDir = DIRECTION_TURN[d][0];
            return this.bb._scan_pair_map[turnDir][2];
        });

        this.turn_direction_steps = this.directions.map(d => [
            DIRECTION_STEPS[DIRECTION_TURN[d][0]],
            DIRECTION_STEPS[DIRECTION_TURN[d][1]]
        ]);

        this.xy_bits = 6;
        this.xy_mask = (1 << this.xy_bits) - 1;
        this.max_pid_gote = this.bb.N_ally_pieces;
        this.board16 = this.bb.board16;
        this.pieceSq16 = this.bb.pieceSq16;

        this._init_moves();
    }

    _init_moves() {
        const sq = this.pieceSq16[this.pid];
        this.x = sq & this.xy_mask;
        this.y = (sq >> this.xy_bits) & this.xy_mask;
        this.board_x = this.bb.board_x;
        this.index = this.y * this.board_x + this.x;

        this.turn_distances = new Array(this.N_directions).fill(0);
        this.after_turn_distances = Array.from({ length: this.N_directions }, () => []);
        this.move_dir_mask = new Map();
        this.reach_edge = new Array(this.N_directions).fill(false);

        for (let i = 0; i < this.N_directions; i++) {
            this._update_turn_distances(i);
        }
    }

    _update_turn_distances(dir_idx: number) {
        const new_distance = this.scan_funcs[dir_idx]();
        const step = this.direction_steps[dir_idx];
        const last_idx = this.index + step * new_distance;

        const last_pid = this.board16[last_idx];
        const old_reach_edge = this.reach_edge[dir_idx];

        let new_turn_distance = 0;
        if (last_pid > 0) {
            this.move_dir_mask.set(last_idx, this.is_piece_mask | (1 << dir_idx));
            new_turn_distance = Math.max(0, new_distance - 1);
            this.reach_edge[dir_idx] = false;
        } else {
            new_turn_distance = new_distance;
            this.reach_edge[dir_idx] = true;
        }

        const old_turn_distance = this.turn_distances[dir_idx];
        if (new_turn_distance > old_turn_distance) {
            this._extend_turn_distances(dir_idx, new_turn_distance);
        } else if (new_turn_distance < old_turn_distance) {
            this._truncate_turn_distances(dir_idx, new_turn_distance, old_reach_edge);
        }
    }

    _extend_turn_distances(dir_idx: number, new_turn_distance: number) {
        const old_turn_distance = this.turn_distances[dir_idx];
        const [dx, dy] = this.direction_vectors[dir_idx];
        const scan_func = this.turn_scan_funcs[dir_idx];
        const [step_lsb, step_msb] = this.turn_direction_steps[dir_idx];
        const dir_bit = 1 << dir_idx;

        const x = this.x;
        const y = this.y;
        const board_x = this.board_x;
        const move_dir_mask = this.move_dir_mask;
        const atd = this.after_turn_distances[dir_idx];

        for (let d = old_turn_distance + 1; d <= new_turn_distance; d++) {
            const turn_y = y + dy * d;
            const turn_x = x + dx * d;
            const [d_lsb, d_msb] = scan_func(turn_x, turn_y, 0);
            atd.push([d_lsb, d_msb]);

            const turn_idx = turn_y * board_x + turn_x;
            move_dir_mask.set(turn_idx, dir_bit);

            const last_index_lsb = turn_idx + step_lsb * d_lsb;
            const last_index_msb = turn_idx + step_msb * d_msb;

            this._extend_second_move(turn_idx, last_index_lsb, step_lsb, dir_bit);
            this._extend_second_move(turn_idx, last_index_msb, step_msb, dir_bit);
        }

        this.turn_distances[dir_idx] = new_turn_distance;
    }

    _extend_second_move(start: number, last: number, step: number, dir_bit: number) {
        const move_dir_mask = this.move_dir_mask;
        const start_mask = move_dir_mask.get(start) || 0;
        move_dir_mask.set(start, start_mask & 0b1111);

        if (step > 0) {
            for (let target_idx = start + step; target_idx < last; target_idx += step) {
                move_dir_mask.set(target_idx, (move_dir_mask.get(target_idx) || 0) | dir_bit);
            }
        } else {
            for (let target_idx = start + step; target_idx > last; target_idx += step) {
                move_dir_mask.set(target_idx, (move_dir_mask.get(target_idx) || 0) | dir_bit);
            }
        }

        const last_pid = this.board16[last];
        const mask = ((last_pid > 0 ? 1 : 0) << 4) | dir_bit;
        move_dir_mask.set(last, (move_dir_mask.get(last) || 0) | mask);
    }

    _truncate_turn_distances(dir_idx: number, new_turn_distance: number, old_reach_edge: boolean) {
        const old_turn_distance = this.turn_distances[dir_idx];
        const [step_lsb, step_msb] = this.turn_direction_steps[dir_idx];
        const dir_bit = 1 << dir_idx;
        const step = this.direction_steps[dir_idx];
        const atd = this.after_turn_distances[dir_idx];

        if (!old_reach_edge) {
            const idx = this.index + (old_turn_distance + 1) * step;
            if (this.move_dir_mask.has(idx)) {
                const new_mask = (this.move_dir_mask.get(idx) || 0) ^ dir_bit;
                if (new_mask & 0b1111) {
                    this.move_dir_mask.set(idx, new_mask);
                } else {
                    this.move_dir_mask.delete(idx);
                }
            }
        }

        for (let d = old_turn_distance; d >= new_turn_distance + 1; d--) {
            const turn_idx = this.index + step * d;
            const [d_lsb, d_msb] = atd[d - 1];
            const last_index_lsb = turn_idx + step_lsb * d_lsb;
            const last_index_msb = turn_idx + step_msb * d_msb;

            this._truncate_second_move(turn_idx, last_index_lsb, step_lsb, dir_bit, false);
            this._truncate_second_move(turn_idx, last_index_msb, step_msb, dir_bit, false);
        }

        this.after_turn_distances[dir_idx] = atd.slice(0, new_turn_distance);
        this.turn_distances[dir_idx] = new_turn_distance;
    }

    _truncate_second_move(start: number, last: number, step: number, dir_bit: number, is_start_piece: boolean = true) {
        const move_dir_mask = this.move_dir_mask;
        if (is_start_piece) {
            move_dir_mask.set(start, (move_dir_mask.get(start) || 0) | this.is_piece_mask);
        }

        if (step > 0) {
            for (let target_idx = start + step; target_idx < last; target_idx += step) {
                const new_mask = (move_dir_mask.get(target_idx) || 0) ^ dir_bit;
                if (new_mask) move_dir_mask.set(target_idx, new_mask);
                else move_dir_mask.delete(target_idx);
            }
        } else {
            for (let target_idx = start + step; target_idx > last; target_idx += step) {
                const new_mask = (move_dir_mask.get(target_idx) || 0) ^ dir_bit;
                if (new_mask) move_dir_mask.set(target_idx, new_mask);
                else move_dir_mask.delete(target_idx);
            }
        }

        const new_mask = (move_dir_mask.get(last) || 0) ^ dir_bit;
        if (new_mask & 0b1111) move_dir_mask.set(last, new_mask);
        else move_dir_mask.delete(last);
    }

    _toggle_piece_occ(target_x: number, target_y: number) {
        target_x = Math.floor(target_x);
        target_y = Math.floor(target_y);
        const target_idx = target_y * this.board_x + target_x;
        const mask = this.move_dir_mask.get(target_idx);

        if (mask !== undefined) {
            const target_dir = get_ray_direction(this.x, this.y, target_x, target_y);
            if (target_dir !== null && this.directions.includes(target_dir)) {
                for (let i = 0; i < this.N_directions; i++) {
                    if (mask & (1 << i)) {
                        this._update_turn_distances(i);
                        break;
                    }
                }
            } else {
                for (let i = 0; i < this.N_directions; i++) {
                    if (mask & (1 << i)) {
                        const [turn_x, turn_y] = get_turn_point(this.directions[i], target_x, target_y, this.x, this.y);
                        const turn_idx = turn_y * this.board_x + turn_x;

                        const is_dir_msb_flag = is_turn_msb(this.directions[i], turn_x, turn_y, target_x, target_y);
                        const is_dir_msb = is_dir_msb_flag ? 1 : 0;
                        const d = Math.max(Math.abs(turn_x - this.x), Math.abs(turn_y - this.y));

                        const step = this.turn_direction_steps[i][is_dir_msb];
                        const atd = this.after_turn_distances[i][d - 1];
                        const old_distance = atd[is_dir_msb];
                        const new_distance = this.turn_scan_funcs[i](turn_x, turn_y, 0)[is_dir_msb];

                        if (new_distance > old_distance) {
                            this._extend_second_move(
                                turn_idx + step * old_distance,
                                turn_idx + step * new_distance,
                                step,
                                (1 << i)
                            );
                        } else if (new_distance < old_distance) {
                            this._truncate_second_move(
                                turn_idx + step * new_distance,
                                turn_idx + step * old_distance,
                                step,
                                (1 << i)
                            );
                        }

                        atd[is_dir_msb] = new_distance;
                    }
                }
            }
        }
    }

    apply_move(pid: number, from_x: number, from_y: number, to_x: number, to_y: number, captured_path: number[] = [], landing_pid: number = 0) {
        if (pid === this.pid) {
            this._init_moves();
        } else {
            if (!(this.pieceSq16[this.pid] & (1 << 13))) {
                return;
            }

            if (from_x === to_x && from_y === to_y) {
                // do nothing for 0 distance
            } else {
                if (landing_pid === 0) {
                    this._toggle_piece_occ(to_x, to_y);
                }
                this._toggle_piece_occ(from_x, from_y);
            }

            for (const cap_pid of captured_path) {
                const sq = this.pieceSq16[cap_pid] & 0xFFF;
                this._toggle_piece_occ(sq & 0x3F, sq >> 6);
            }
        }
    }

    undo_move(pid: number, from_x: number, from_y: number, to_x: number, to_y: number, captured_path: number[] = [], landing_pid: number = 0) {
        if (pid === this.pid) {
            this._init_moves();
        } else {
            if (!(this.pieceSq16[this.pid] & (1 << 13))) {
                return;
            }

            if (landing_pid === this.pid || captured_path.includes(this.pid)) {
                return;
            }

            if (from_x === to_x && from_y === to_y) {
                // do nothing
            } else {
                this._toggle_piece_occ(from_x, from_y);
            }

            for (let i = captured_path.length - 1; i >= 0; i--) {
                const cap_pid = captured_path[i];
                const sq = this.pieceSq16[cap_pid] & 0xFFF;
                this._toggle_piece_occ(sq & 0x3F, sq >> 6);
            }

            if (!(from_x === to_x && from_y === to_y)) {
                if (landing_pid === 0) {
                    this._toggle_piece_occ(to_x, to_y);
                }
            }
        }
    }
}

export class KougyouMove extends DoubleRayMove {
    constructor(bitboard: BoardState, pid: number) {
        super(bitboard, pid, [BoardConst.TOP, BoardConst.RIGHT, BoardConst.BOTTOM, BoardConst.LEFT]);
    }
}

export class TenguMove extends DoubleRayMove {
    constructor(bitboard: BoardState, pid: number) {
        super(bitboard, pid, [BoardConst.TOP_RIGHT, BoardConst.BOTTOM_RIGHT, BoardConst.BOTTOM_LEFT, BoardConst.TOP_LEFT]);
    }
}

export class SenteKujakuMove extends DoubleRayMove {
    constructor(bitboard: BoardState, pid: number) {
        super(bitboard, pid, [BoardConst.TOP_RIGHT, BoardConst.TOP_LEFT]);
    }
}

export class GoteKujakuMove extends DoubleRayMove {
    constructor(bitboard: BoardState, pid: number) {
        super(bitboard, pid, [BoardConst.BOTTOM_LEFT, BoardConst.BOTTOM_RIGHT]);
    }
}

export class ShishiMove {
    ADJACENT_OFFSETS = [
        [0, -1], [1, -1], [1, 0], [1, 1],
        [0, 1], [-1, 1], [-1, 0], [-1, -1],
    ];

    JUMP_OFFSETS = [
        [-2, -2], [-1, -2], [0, -2], [1, -2], [2, -2],
        [-2, -1],                           [2, -1],
        [-2,  0],                           [2,  0],
        [-2,  1],                           [2,  1],
        [-2,  2], [-1,  2], [0,  2], [1,  2], [2,  2],
    ];

    bb: BoardState;
    pid: number;
    xy_bits: number;
    xy_mask: number;
    board_x: number;

    constructor(bitboard: BoardState, pid: number) {
        this.bb = bitboard;
        this.pid = pid;
        this.xy_bits = 6;
        this.xy_mask = (1 << this.xy_bits) - 1;
        this.board_x = BOARD_X;
    }

    _get_piece_xy(): [number, number] {
        const sq = this.bb.pieceSq16[this.pid];
        const x = sq & this.xy_mask;
        const y = (sq >> this.xy_bits) & this.xy_mask;
        return [x, y];
    }

    get_first_moves(side: number, board: Int16Array): any[] {
        const [x, y] = this._get_piece_xy();
        const moves = [];

        for (const [dx, dy] of this.ADJACENT_OFFSETS) {
            const tx = x + dx;
            const ty = y + dy;
            if (tx < 0 || tx >= BOARD_X || ty < 0 || ty >= BOARD_Y) continue;
            const target_pid = board[ty * BOARD_X + tx];
            if (target_pid === 0) {
                moves.push({ x: tx, y: ty, type: 'empty', is_jump: false });
            } else {
                moves.push({ x: tx, y: ty, type: '_piece', is_jump: false, target_pid: target_pid });
            }
        }

        for (const [dx, dy] of this.JUMP_OFFSETS) {
            const tx = x + dx;
            const ty = y + dy;
            if (tx < 0 || tx >= BOARD_X || ty < 0 || ty >= BOARD_Y) continue;
            const target_pid = board[ty * BOARD_X + tx];
            if (target_pid === 0) {
                moves.push({ x: tx, y: ty, type: 'empty', is_jump: true });
            } else {
                moves.push({ x: tx, y: ty, type: '_piece', is_jump: true, target_pid: target_pid });
            }
        }
        return moves;
    }

    get_second_moves(first_x: number, first_y: number, side: number, board: Int16Array, original_x: number | null = null, original_y: number | null = null): any[] {
        const moves = [];
        moves.push({ x: first_x, y: first_y, type: 'stay' });

        for (const [dx, dy] of this.ADJACENT_OFFSETS) {
            const tx = first_x + dx;
            const ty = first_y + dy;
            if (tx < 0 || tx >= BOARD_X || ty < 0 || ty >= BOARD_Y) continue;

            if (original_x !== null && tx === original_x && ty === original_y) {
                moves.push({ x: tx, y: ty, type: 'empty' });
                continue;
            }

            const target_pid = board[ty * BOARD_X + tx];
            if (target_pid === 0) {
                moves.push({ x: tx, y: ty, type: 'empty' });
            } else {
                moves.push({ x: tx, y: ty, type: '_piece', target_pid: target_pid });
            }
        }
        return moves;
    }

    apply_move(pid: number, from_x: number, from_y: number, to_x: number, to_y: number, captured_path: number[] = [], landing_pid: number = 0) {}

    undo_move(pid: number, from_x: number, from_y: number, to_x: number, to_y: number, captured_path: number[] = [], landing_pid: number = 0) {}
}

export class HonjuuMove {
    SENTE_FORWARD_DIAG = new Set([BoardConst.TOP_RIGHT, BoardConst.TOP_LEFT]);
    GOTE_FORWARD_DIAG = new Set([BoardConst.BOTTOM_RIGHT, BoardConst.BOTTOM_LEFT]);

    ALL_DIRECTIONS = [
        BoardConst.TOP, BoardConst.TOP_RIGHT, BoardConst.RIGHT,
        BoardConst.BOTTOM_RIGHT, BoardConst.BOTTOM, BoardConst.BOTTOM_LEFT,
        BoardConst.LEFT, BoardConst.TOP_LEFT,
    ];

    bb: BoardState;
    pid: number;
    xy_bits: number;
    xy_mask: number;
    board_x: number;

    constructor(bitboard: BoardState, pid: number) {
        this.bb = bitboard;
        this.pid = pid;
        this.xy_bits = 6;
        this.xy_mask = (1 << this.xy_bits) - 1;
        this.board_x = BOARD_X;
    }

    _get_piece_xy(): [number, number] {
        const sq = this.bb.pieceSq16[this.pid];
        const x = sq & this.xy_mask;
        const y = (sq >> this.xy_bits) & this.xy_mask;
        return [x, y];
    }

    get_max_steps(direction: BoardConst, side: number): number {
        const forward_diag = side === 0 ? this.SENTE_FORWARD_DIAG : this.GOTE_FORWARD_DIAG;
        return forward_diag.has(direction) ? 4 : 3;
    }

    get_first_moves(side: number, board: Int16Array): any[] {
        const [x, y] = this._get_piece_xy();
        const moves = [];

        for (const direction of this.ALL_DIRECTIONS) {
            const [dx, dy] = DIRECTION_VECTORS[direction];
            const max_steps = this.get_max_steps(direction, side);

            for (let d = 1; d <= max_steps; d++) {
                const tx = x + dx * d;
                const ty = y + dy * d;
                if (tx < 0 || tx >= BOARD_X || ty < 0 || ty >= BOARD_Y) break;

                const is_final = (d === max_steps);
                const target_pid = board[ty * BOARD_X + tx];

                if (target_pid === 0) {
                    moves.push({ x: tx, y: ty, type: 'empty', is_final, direction, distance: d, max_steps });
                } else {
                    moves.push({ x: tx, y: ty, type: '_piece', target_pid, is_final, direction, distance: d, max_steps });
                }
            }
        }
        return moves;
    }

    get_next_moves(direction: BoardConst, current_x: number, current_y: number, remaining_steps: number, origin_x: number, origin_y: number, side: number, board: Int16Array, current_distance_from_origin: number): any[] {
        const moves = [];
        moves.push({ x: current_x, y: current_y, type: 'stay', new_distance: current_distance_from_origin });

        if (remaining_steps <= 0) return moves;

        const [dx, dy] = DIRECTION_VECTORS[direction];

        // 前進
        const tx = current_x + dx;
        const ty = current_y + dy;
        const new_dist = current_distance_from_origin + 1;
        if (tx >= 0 && tx < BOARD_X && ty >= 0 && ty < BOARD_Y) {
            const target_pid = board[ty * BOARD_X + tx];
            const is_final = (remaining_steps === 1);
            if (target_pid === 0) {
                moves.push({ x: tx, y: ty, type: 'empty', is_final, new_distance: new_dist });
            } else {
                moves.push({ x: tx, y: ty, type: '_piece', target_pid, is_final, new_distance: new_dist });
            }
        }

        // 後退
        if (current_distance_from_origin > 0) {
            const bx = current_x - dx;
            const by = current_y - dy;
            const back_dist = current_distance_from_origin - 1;
            if (bx >= 0 && bx < BOARD_X && by >= 0 && by < BOARD_Y) {
                if (bx === origin_x && by === origin_y) {
                    moves.push({ x: bx, y: by, type: 'empty', is_final: (remaining_steps === 1), new_distance: back_dist });
                } else {
                    const target_pid = board[by * BOARD_X + bx];
                    if (target_pid === 0) {
                        moves.push({ x: bx, y: by, type: 'empty', is_final: (remaining_steps === 1), new_distance: back_dist });
                    } else {
                        moves.push({ x: bx, y: by, type: '_piece', target_pid, is_final: (remaining_steps === 1), new_distance: back_dist });
                    }
                }
            }
        }
        return moves;
    }

    apply_move(pid: number, from_x: number, from_y: number, to_x: number, to_y: number, captured_path: number[] = [], landing_pid: number = 0) {}

    undo_move(pid: number, from_x: number, from_y: number, to_x: number, to_y: number, captured_path: number[] = [], landing_pid: number = 0) {}
}
